import { motion } from "framer-motion";
import { LineReveal, FadeUp } from "../components/RevealText";

export default function About() {
  return (
    <main data-testid="about-page" className="bg-black text-white pt-24">
      {/* Editorial Header */}
      <section className="relative border-b border-white/10 px-4 md:px-6 py-10 md:py-14">
        <h1 className="font-display font-bold text-6xl md:text-8xl leading-[0.9] uppercase">
          <LineReveal delay={0.15}>About Us</LineReveal>
        </h1>
        <div className="grid grid-cols-12 gap-6 mt-8">
          <div className="col-span-12 md:col-span-6">
            <p className="text-white/70 text-base md:text-lg leading-relaxed">
              Since 1965, Anron Air Systems has installed, fabricated, and
              serviced heavy commercial HVAC across Long Island and the Metro
              Area. We are not a general contractor. We are metal-benders,
              welders, riggers, and technicians — a shop that shows up.
            </p>
          </div>
        </div>
      </section>

      {/* Big Image Block */}
      <section className="relative border-b border-white/10 grid grid-cols-1 md:grid-cols-2 gap-0">
        <div className="relative h-[45vh] md:h-[70vh] overflow-hidden md:border-r border-white/10">
          <motion.img
            initial={{ scale: 1.08 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 2, ease: [0.76, 0, 0.24, 1] }}
            src="/assets/anron_hq_1.webp"
            alt="Anron Air Systems — 440 Wyandanch Avenue entrance"
            data-testid="about-hero-image-1"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative h-[45vh] md:h-[70vh] overflow-hidden">
          <motion.img
            initial={{ scale: 1.08 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 2, delay: 0.1, ease: [0.76, 0, 0.24, 1] }}
            src="/assets/anron_hq_2.webp"
            alt="Anron stone signage — Heating · Air Conditioning · Sheet Metal · Service"
            data-testid="about-hero-image-2"
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      {/* About the Anron Family of Companies */}
      <section
        data-testid="anron-family-section"
        className="relative border-b border-white/10 px-4 md:px-6 py-10 md:py-14"
      >
        <div className="grid grid-cols-12 gap-6">
          <FadeUp className="col-span-12 md:col-span-10 md:col-start-2 space-y-6 text-white/75 text-lg md:text-xl leading-relaxed">
            <p>
              Founded in 1965 and privately owned and operated, today the
              ANRON family of companies is among the largest, most innovative,
              and highly-respected full-service HVAC manufacturing,
              installation, and service organizations in metropolitan New
              York.
            </p>
            <p>
              ANRON is proud to be the HVAC contractor of choice for an
              ever-growing client base within our service territory, which
              embraces the five boroughs of New York City and Nassau and
              Suffolk Counties on Long Island. This diverse client base
              includes the region&rsquo;s premier construction companies and
              building, facilities, and institutional owners and managers for
              which ANRON has provided service on every type of project, from
              office buildings and corporate headquarters to shopping malls,
              hospitals, schools, research and development laboratories,
              pharmaceutical laboratories, correctional facilities, and more.
            </p>
            <p>
              Serving clients with a unique, vertically-integrated menu of
              HVAC, sheet metal fabrication, and systems maintenance services
              through{" "}
              <span className="text-white font-medium">
                ANRON Air Systems, Inc.
              </span>{" "}
              and{" "}
              <span className="text-white font-medium">
                ANRON Mechanical Services, Inc.
              </span>
            </p>
            <p>
              This vertical integration of manufacturing, installation, and
              service in a &lsquo;one-stop shop&rsquo; empowers ANRON to
              provide consistently superior workmanship and service at the
              industry&rsquo;s most competitive prices. Clients benefit from
              and appreciate knowing that the vertical integration of ANRON
              services minimizes the uncertainties and variables associated
              with major capital project work while maximizing accountability,
              cost-effectiveness, job control, and results.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* Ron Spinelli - Founder Memorial */}
      <section
        data-testid="founder-memorial"
        className="relative border-b border-white/10 px-4 md:px-6 py-14 md:py-20"
      >
        <div className="grid grid-cols-12 gap-6 md:gap-10 items-start">
          <div className="col-span-12 md:col-span-4 md:pl-8 md:pt-8">
            <FadeUp>
              <div className="relative aspect-[3/4] w-full max-w-sm border border-white/15 overflow-hidden">
                <img
                  src="/assets/ron_spinelli.jpg"
                  alt="Ronald Spinelli — ANRON Founder"
                  data-testid="founder-image"
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-white/95">
                  <span>Ronald Spinelli</span>
                </div>
              </div>
            </FadeUp>
          </div>

          <div className="col-span-12 md:col-span-7 md:col-start-6">
            <FadeUp>
              <h2 className="font-display font-bold text-5xl md:text-7xl leading-[0.9] uppercase">
                Ronald Spinelli
              </h2>
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/60 mt-3">
                ANRON Founder
              </p>
              <p className="font-display text-2xl md:text-3xl text-white/70 mt-2">
                1937 <span className="text-white/40 mx-2">—</span> 2004
              </p>
            </FadeUp>

            <FadeUp delay={0.1} className="mt-6 space-y-5 text-white/75 text-lg md:text-xl leading-relaxed max-w-2xl">
              <p>
                An acknowledged authority in all aspects of HVAC, and a
                visionary industry innovator and entrepreneur, Ronald Spinelli
                originally founded the ANRON Family of Companies as ANRON Air
                Systems, Inc. in 1965.
              </p>
              <p>
                ANRON pledges to honor the life and work of Ronald Spinelli by
                incorporating our founder&rsquo;s personal and professional
                values of integrity and service in everything we do.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Project Photos */}
      <section
        data-testid="about-photos"
        className="border-b border-white/10 px-4 md:px-6 py-10 md:py-14"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <FadeUp>
            <div className="relative aspect-[3/4] w-full overflow-hidden border border-white/10">
              <img
                src="/assets/project1.jpg"
                alt=""
                aria-hidden="true"
                data-testid="about-photo-1"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          </FadeUp>
          <FadeUp delay={0.08}>
            <div className="relative aspect-[3/4] w-full overflow-hidden border border-white/10">
              <img
                src="/assets/project2.jpg"
                alt=""
                aria-hidden="true"
                data-testid="about-photo-2"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          </FadeUp>
          <FadeUp delay={0.16}>
            <div className="relative aspect-[3/4] w-full overflow-hidden border border-white/10">
              <img
                src="/assets/project4.jpg"
                alt=""
                aria-hidden="true"
                data-testid="about-photo-3"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          </FadeUp>
          <FadeUp delay={0.24}>
            <div className="relative aspect-[3/4] w-full overflow-hidden border border-white/10">
              <img
                src="/assets/project3.jpg"
                alt=""
                aria-hidden="true"
                data-testid="about-photo-4"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
